<template>
  <div
    class="dh-input"
    :class="[
      {
        'dh-input--disabled': disabled,
        'dh-input--readonly': readonly,
        'dh-input--clearable': clearable && !disabled && !readonly,
        'dh-input--password': showPassword,
        'dh-input--with-prefix': $slots.prefix || prefixIcon,
        'dh-input--with-suffix': $slots.suffix || suffixIcon || clearable || showPassword,
        'dh-input--focused': focused,
        'dh-input--error': validateState === 'error'
      },
      `dh-input--${size}`
    ]"
  >
    <!-- 前置内容 -->
    <div v-if="$slots.prepend" class="dh-input__prepend">
      <slot name="prepend"></slot>
    </div>

    <!-- 输入框容器 -->
    <div class="dh-input__wrapper">
      <!-- 前缀图标 -->
      <span v-if="$slots.prefix || prefixIcon" class="dh-input__prefix">
        <slot name="prefix">
          <ElIcon v-if="prefixIcon">
            <component :is="prefixIcon" />
          </ElIcon>
        </slot>
      </span>

      <!-- 输入框 -->
      <input
        ref="inputRef"
        v-model="inputValue"
        :type="inputType"
        class="dh-input__inner"
        :class="[
          {
            'dh-input__inner--prefix': $slots.prefix || prefixIcon,
            'dh-input__inner--suffix': $slots.suffix || suffixIcon || clearable || showPassword
          }
        ]"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
        :minlength="minlength"
        :autocomplete="autocomplete"
        :name="name"
        :form="form"
        :tabindex="tabindex"
        :autofocus="autofocus"
        :step="step"
        :min="min"
        :max="max"
        @input="handleInput"
        @change="handleChange"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
        @keyup="handleKeyup"
        @keypress="handleKeypress"
        @compositionstart="handleCompositionStart"
        @compositionupdate="handleCompositionUpdate"
        @compositionend="handleCompositionEnd"
      />

      <!-- 后缀内容 -->
      <span
        v-if="$slots.suffix || suffixIcon || clearable || showPassword || showWordLimit"
        class="dh-input__suffix"
      >
        <!-- 清除按钮 -->
        <ElIcon
          v-if="clearable && !disabled && !readonly && inputValue"
          class="dh-input__clear"
          @click="handleClear"
        >
          <CircleClose />
        </ElIcon>

        <!-- 密码显示切换 -->
        <ElIcon
          v-if="showPassword && !disabled && !readonly"
          class="dh-input__password"
          @click="togglePasswordVisibility"
        >
          <View v-if="passwordVisible" />
          <Hide v-else />
        </ElIcon>

        <!-- 后缀图标 -->
        <ElIcon v-if="suffixIcon && !clearable && !showPassword">
          <component :is="suffixIcon" />
        </ElIcon>

        <!-- 自定义后缀 -->
        <slot name="suffix"></slot>
      </span>

      <!-- 字数统计 -->
      <span v-if="showWordLimit" class="dh-input__count">
        <span
          :class="{
            'dh-input__count--error': inputLength > maxlength
          }"
        >
          {{ inputLength }}
        </span>
        <span v-if="maxlength">{{ `/${maxlength}` }}</span>
      </span>
    </div>

    <!-- 后置内容 -->
    <div v-if="$slots.append" class="dh-input__append">
      <slot name="append"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Input 输入框组件
 * 基于 Element Plus Input 组件封装，提供更多自定义功能
 * @author AI Assistant
 * @date 2024-01-01
 */

import { ref, computed, watch, nextTick, onMounted } from 'vue'
import type { Component } from 'vue'
import type { ComponentSize } from 'element-plus'
import { CircleClose, View, Hide } from '@element-plus/icons-vue'

// 接口定义
interface Props {
  /** 输入框值 */
  modelValue?: string | number
  /** 输入框类型 */
  type?: 'text' | 'password' | 'number' | 'email' | 'url' | 'tel' | 'search'
  /** 占位符 */
  placeholder?: string
  /** 是否禁用 */
  disabled?: boolean
  /** 是否只读 */
  readonly?: boolean
  /** 是否可清空 */
  clearable?: boolean
  /** 是否显示密码切换按钮 */
  showPassword?: boolean
  /** 是否显示字数统计 */
  showWordLimit?: boolean
  /** 最大长度 */
  maxlength?: number
  /** 最小长度 */
  minlength?: number
  /** 自动完成 */
  autocomplete?: string
  /** 输入框名称 */
  name?: string
  /** 表单名称 */
  form?: string
  /** tab索引 */
  tabindex?: number
  /** 是否自动聚焦 */
  autofocus?: boolean
  /** 数字输入步长 */
  step?: number
  /** 最小值 */
  min?: number
  /** 最大值 */
  max?: number
  /** 组件大小 */
  size?: ComponentSize
  /** 前缀图标 */
  prefixIcon?: Component
  /** 后缀图标 */
  suffixIcon?: Component
  /** 验证状态 */
  validateState?: 'success' | 'warning' | 'error'
  /** 格式化函数 */
  formatter?: (value: string | number) => string
  /** 解析函数 */
  parser?: (value: string) => string
  /** 输入时是否触发表单验证 */
  validateEvent?: boolean
  /** 输入法输入时是否触发 input 事件 */
  inputStyle?: Record<string, any>
}

interface Emits {
  /** 值更新事件 */
  'update:modelValue': [value: string | number]
  /** 输入事件 */
  'input': [value: string | number]
  /** 值改变事件 */
  'change': [value: string | number]
  /** 获得焦点事件 */
  'focus': [event: FocusEvent]
  /** 失去焦点事件 */
  'blur': [event: FocusEvent]
  /** 清空事件 */
  'clear': []
  /** 键盘按下事件 */
  'keydown': [event: KeyboardEvent]
  /** 键盘抬起事件 */
  'keyup': [event: KeyboardEvent]
  /** 键盘按键事件 */
  'keypress': [event: KeyboardEvent]
}

// 定义 props
const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  type: 'text',
  placeholder: '',
  disabled: false,
  readonly: false,
  clearable: false,
  showPassword: false,
  showWordLimit: false,
  autocomplete: 'off',
  tabindex: 0,
  autofocus: false,
  size: 'default',
  validateEvent: true
})

// 定义 emits
const emit = defineEmits<Emits>()

// 响应式数据
const inputRef = ref<HTMLInputElement>()
const focused = ref(false)
const passwordVisible = ref(false)
const isComposing = ref(false)

// 计算属性
const inputValue = computed({
  get() {
    return props.modelValue
  },
  set(value: string | number) {
    emit('update:modelValue', value)
  }
})

const inputType = computed(() => {
  if (props.type === 'password') {
    return passwordVisible.value ? 'text' : 'password'
  }
  return props.type
})

const inputLength = computed(() => {
  return String(inputValue.value || '').length
})

const nativeInputValue = computed(() => {
  return inputValue.value === null || inputValue.value === undefined ? '' : String(inputValue.value)
})

// 方法定义
/**
 * 设置原生输入框的值
 */
const setNativeInputValue = (): void => {
  const input = inputRef.value
  if (!input || input.value === nativeInputValue.value) return
  input.value = nativeInputValue.value
}

/**
 * 处理输入事件
 */
const handleInput = (event: Event): void => {
  const target = event.target as HTMLInputElement
  let { value } = target

  if (isComposing.value) return

  // 应用解析器
  if (props.parser) {
    value = props.parser(value)
  }

  // 应用格式化器
  if (props.formatter) {
    value = props.formatter(value)
  }

  if (value === nativeInputValue.value) {
    setNativeInputValue()
    return
  }

  emit('input', value)
  emit('update:modelValue', value)

  nextTick(() => {
    setNativeInputValue()
  })
}

/**
 * 处理值改变事件
 */
const handleChange = (event: Event): void => {
  const target = event.target as HTMLInputElement
  emit('change', target.value)
}

/**
 * 处理获得焦点事件
 */
const handleFocus = (event: FocusEvent): void => {
  focused.value = true
  emit('focus', event)
}

/**
 * 处理失去焦点事件
 */
const handleBlur = (event: FocusEvent): void => {
  focused.value = false
  emit('blur', event)
}

/**
 * 处理清空事件
 */
const handleClear = (): void => {
  emit('input', '')
  emit('update:modelValue', '')
  emit('clear')
  focus()
}

/**
 * 切换密码可见性
 */
const togglePasswordVisibility = (): void => {
  passwordVisible.value = !passwordVisible.value
  focus()
}

/**
 * 处理键盘事件
 */
const handleKeydown = (event: KeyboardEvent): void => {
  emit('keydown', event)
}

const handleKeyup = (event: KeyboardEvent): void => {
  emit('keyup', event)
}

const handleKeypress = (event: KeyboardEvent): void => {
  emit('keypress', event)
}

/**
 * 处理输入法事件
 */
const handleCompositionStart = (): void => {
  isComposing.value = true
}

const handleCompositionUpdate = (): void => {
  // 输入法更新
}

const handleCompositionEnd = (event: CompositionEvent): void => {
  isComposing.value = false
  handleInput(event)
}

/**
 * 聚焦输入框
 */
const focus = (): void => {
  nextTick(() => {
    inputRef.value?.focus()
  })
}

/**
 * 失焦输入框
 */
const blur = (): void => {
  inputRef.value?.blur()
}

/**
 * 选中输入框内容
 */
const select = (): void => {
  inputRef.value?.select()
}

// 监听器
watch(
  () => props.modelValue,
  () => {
    nextTick(() => {
      setNativeInputValue()
    })
  }
)

// 生命周期
onMounted(() => {
  setNativeInputValue()

  if (props.autofocus) {
    focus()
  }
})

// 暴露方法
defineExpose({
  focus,
  blur,
  select,
  input: inputRef
})
</script>

<style scoped>
.dh-input {
  position: relative;
  display: inline-flex;
  width: 100%;
  vertical-align: middle;
  font-size: var(--el-font-size-base);
}

.dh-input__wrapper {
  position: relative;
  display: inline-flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  padding: 1px 11px;
  background-color: var(--el-input-bg-color, var(--el-fill-color-blank));
  background-image: none;
  border-radius: var(--el-input-border-radius, var(--el-border-radius-base));
  cursor: text;
  transition: var(--el-transition-box-shadow);
  transform: translate3d(0, 0, 0);
  box-shadow: 0 0 0 1px var(--el-input-border-color, var(--el-border-color)) inset;
}

.dh-input__inner {
  width: 100%;
  flex-grow: 1;
  appearance: none;
  color: var(--el-input-text-color, var(--el-text-color-regular));
  font-size: inherit;
  height: var(--el-input-inner-height);
  line-height: var(--el-input-inner-height);
  padding: 0;
  outline: none;
  border: none;
  background: none;
  box-sizing: border-box;
}

.dh-input__inner::placeholder {
  color: var(--el-input-placeholder-color, var(--el-text-color-placeholder));
}

.dh-input__prefix,
.dh-input__suffix {
  position: absolute;
  top: 0;
  height: 100%;
  color: var(--el-input-icon-color, var(--el-text-color-placeholder));
  text-align: center;
  transition: all var(--el-transition-duration);
  pointer-events: none;
  display: flex;
  align-items: center;
}

.dh-input__prefix {
  left: 8px;
}

.dh-input__suffix {
  right: 8px;
  pointer-events: auto;
}

.dh-input__inner--prefix {
  padding-left: 30px;
}

.dh-input__inner--suffix {
  padding-right: 30px;
}

.dh-input__clear,
.dh-input__password {
  cursor: pointer;
  transition: color var(--el-transition-duration);
}

.dh-input__clear:hover,
.dh-input__password:hover {
  color: var(--el-input-clear-hover-color, var(--el-text-color-secondary));
}

.dh-input__count {
  position: absolute;
  right: 8px;
  bottom: -20px;
  font-size: 12px;
  color: var(--el-text-color-placeholder);
  white-space: nowrap;
}

.dh-input__count--error {
  color: var(--el-color-danger);
}

.dh-input__prepend,
.dh-input__append {
  background-color: var(--el-fill-color-light);
  color: var(--el-color-info);
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  border-radius: var(--el-input-border-radius);
  padding: 0 20px;
  white-space: nowrap;
  border: 1px solid var(--el-input-border-color);
}

.dh-input__prepend {
  border-right: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.dh-input__append {
  border-left: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

/* 尺寸变体 */
.dh-input--large {
  font-size: var(--el-font-size-medium);
}

.dh-input--large .dh-input__wrapper {
  padding: 1px 15px;
}

.dh-input--large .dh-input__inner {
  height: var(--el-component-size-large);
  line-height: var(--el-component-size-large);
}

.dh-input--small {
  font-size: var(--el-font-size-small);
}

.dh-input--small .dh-input__wrapper {
  padding: 1px 7px;
}

.dh-input--small .dh-input__inner {
  height: var(--el-component-size-small);
  line-height: var(--el-component-size-small);
}

/* 状态样式 */
.dh-input--focused .dh-input__wrapper {
  box-shadow: 0 0 0 1px var(--el-color-primary) inset;
}

.dh-input--disabled {
  cursor: not-allowed;
}

.dh-input--disabled .dh-input__wrapper {
  background-color: var(--el-disabled-bg-color);
  box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
  cursor: not-allowed;
}

.dh-input--disabled .dh-input__inner {
  color: var(--el-disabled-text-color);
  cursor: not-allowed;
}

.dh-input--disabled .dh-input__inner::placeholder {
  color: var(--el-text-color-disabled);
}

.dh-input--readonly .dh-input__wrapper {
  box-shadow: 0 0 0 1px var(--el-input-border-color) inset;
}

.dh-input--error .dh-input__wrapper {
  box-shadow: 0 0 0 1px var(--el-color-danger) inset;
}

/* 悬停效果 */
.dh-input:hover:not(.dh-input--disabled) .dh-input__wrapper {
  box-shadow: 0 0 0 1px var(--el-input-hover-border-color) inset;
}

/* 清除和密码图标间距 */
.dh-input__suffix .el-icon + .el-icon {
  margin-left: 6px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .dh-input {
    font-size: 16px; /* 防止iOS缩放 */
  }
}

/* 暗色主题适配 */
@media (prefers-color-scheme: dark) {
  .dh-input__wrapper {
    background-color: var(--el-bg-color-page);
  }
}

/* 高对比度模式 */
@media (prefers-contrast: high) {
  .dh-input__wrapper {
    border-width: 2px;
  }

  .dh-input--focused .dh-input__wrapper {
    border-width: 3px;
  }
}

/* 减少动画模式 */
@media (prefers-reduced-motion: reduce) {
  .dh-input__wrapper,
  .dh-input__clear,
  .dh-input__password {
    transition: none;
  }
}

/* 打印样式 */
@media print {
  .dh-input {
    break-inside: avoid;
  }

  .dh-input__clear,
  .dh-input__password {
    display: none;
  }
}
</style>
